import _Object$keys from "babel-runtime/core-js/object/keys";
import _extends from "babel-runtime/helpers/extends";
import { wrapSelectors, extractPath } from "@rosetta/redux-utils";
import * as sreactions from "./sreactions";

import { SrePlaySound, SreReadingTracker } from "./sessions";

import SreAudioQualityModals from "./audio-quality-modals";

import { unique } from "./util";

export var actions = sreactions;

var initial = _extends({
  loading: false, // Is the SRE or speech model currently loading?
  initialized: false, // Is the SRE ready to use right now?
  loadingPercent: 0, // During load, a percent
  error: "", // Error message from SRE
  audioLevel: 0, // Audio level while listening (only works for calibrate and listen for phrases right now)
  listening: false, // Are we currently listening to the mic?
  currentPromptId: "", // What on-screen prompt is currently listening? (special value of CALIBRATE as well)
  scores: {}, // map of prompt ID to score for that prompt.
  wordScores: {}, // map of prompt ID to word scores for that prompt.
  calibrated: false, // Has the user gone through calibration?
  calibrateResult: "", // What was that calibration result?
  calibrateError: null, // Calibration errored. Contains an object with error and session data information.
  micAccess: null, // Result of the mic access check, a string.
  availableMicrophones: [], // list of available mics
  selectedMicrophone: null,
  hypothesis: "",
  saveSoundLogID: null, // ListenForPhrases Unique identifier of uploaded soundlog (if applicable)
  recentlySavedSoundLogIDs: [], // for the SubmitSreReport audio quality issue modal, so can send all recent sound log ids (requires clearning out at this time)
  audioQualityMetrics: null, // ListenForPhrases session audio quality on final result
  speechEnabled: true,
  calibrateHelp: false,
  exitOnCancelCalibrate: true,
  srePath: "/sre/",

  calibratingMicrophone: false, // is the calibrate window open?
  fakeSRE: false, // fake SRE for automation testing.

  speechModelLoaded: false, // Has just the speech model loaded yet?
  sonicLoaded: false, // Has just the sonic lib loaded yet?
  lastSpeechModelLoaded: null, // Which speech model was loaded last time?

  failureCount: 0,

  isBrowserMicBlockModalVisible: false, // user block mic in browser - modal state
  browserMicBlockModalChoice: null, // choice the user makes when closing modal - acknowledge or fix?
  version: ""

}, SrePlaySound.initialState, SreReadingTracker.initialState, SreAudioQualityModals.initialState);

var calibrateReducer = function calibrateReducer(state, action) {
  switch (action.type) {
    case actions.SRE_CALIBRATE_HELP:
      return _extends({}, state, { calibrateHelp: true });

    case actions.SRE_CALIBRATE_CLOSE_HELP:
      return _extends({}, state, { calibrateHelp: false });

    case actions.SRE_OPEN_CALIBRATE_WIN:
      return _extends({}, state, {
        srePath: action.payload.srePath || state.srePath,
        exitOnCancelCalibrate: action.payload.exitOnCancel,
        exitOnDisableSpeechCalibrate: action.payload.exitOnDisableSpeech,
        calibratingMicrophone: true,
        listening: false,
        calibrated: false,
        currentPromptId: "",
        audioLevel: 0,
        calibrateResult: "",
        failureCount: 0
      });

    case actions.SRE_CLOSE_CALIBRATE_WIN:
      return _extends({}, state, { calibratingMicrophone: false });

    case actions.SRE_CALIBRATE_START:
      return _extends({}, state, {
        listening: true,
        currentPromptId: "CALIBRATE",
        calibrateError: null,
        audioLevel: 0
      });

    case actions.SRE_CALIBRATE_ERROR:
      {
        return _extends({}, state, {
          calibrateError: action.payload.error
        });
      }

    case actions.SRE_CALIBRATE_COMPLETE:
      var calibrated = action.payload.qualityStr === "ok";
      return _extends({}, state, {
        listening: false,
        failureCount: state.failureCount + (calibrated ? 0 : 1),
        calibrated: calibrated,
        currentPromptId: "CALIBRATE",
        audioLevel: 0,
        calibrateResult: action.payload.qualityStr
      });
    /* istanbul ignore next */
    default:
      return state;
  }
};

var listenReducer = function listenReducer(state, action) {
  var _extends2, _extends3, _extends4;

  switch (action.type) {
    case actions.SRE_ABORT_LISTEN:
      return _extends({}, state, {
        audioLevel: 0,
        audioQualityMetrics: undefined,
        hypothesis: "",
        listening: false,
        currentPromptId: ""
      });

    case actions.SRE_LISTEN_FOR_PHRASES:
      return _extends({}, state, {
        audioLevel: 0,
        audioQualityMetrics: undefined,
        currentPromptId: action.payload.promptId,
        hypothesis: "",
        listening: true,
        wordScores: _extends({}, state.wordScores, (_extends2 = {}, _extends2[action.payload.promptId] = undefined, _extends2))
      });

    case actions.SRE_LISTEN_FOR_PHRASE_RESULT:
      if (action.payload.canceled) {
        // This happens during situations like the session was canceled because the app was paused.
        return state;
      }

      return _extends({}, state, {
        audioLevel: 0,
        audioQualityMetrics: _extends({}, action.payload.audioQualityMetrics),
        currentPromptId: "",
        listening: false,
        saveSoundLogID: action.payload.saveSoundLogID,
        recentlySavedSoundLogIDs: [].concat(state.recentlySavedSoundLogIDs, [action.payload.saveSoundLogID]),
        scores: _extends({}, state.scores, (_extends3 = {}, _extends3[action.payload.promptId] = action.payload.result, _extends3)),
        wordScores: _extends({}, state.wordScores, (_extends4 = {}, _extends4[action.payload.promptId] = action.payload.wordResult, _extends4))
      });

    case actions.SRE_LISTEN_FOR_PHRASES_RESET_WORDS_RESULT_CACHE:
      return _extends({}, state, {
        wordScores: {}
      });

    case actions.SRE_RESET_RECENTLY_SAVED_SOUND_LOG_IDS_CACHE:
      return _extends({}, state, {
        recentlySavedSoundLogIDs: []
      });

    /* istanbul ignore next */
    default:
      return state;
  }
};

var initSREReducer = function initSREReducer(state, action) {
  switch (action && action.type) {
    case actions.SRE_SONIC_LOAD_COMPLETE:
      return _extends({}, state, {
        sonicLoaded: true
      });

    case actions.SRE_SPEECH_MODEL_LOAD_COMPLETE:
      return _extends({}, state, {
        loading: false,
        lastSpeechModelLoaded: action.payload.modelLoaded,
        speechModelLoaded: true
      });

    case actions.INIT_SRE:
      if (state.loading) return state;
      if (state.initialized) return state;
      return _extends({}, state, {
        loading: true,
        loadingPercent: 0,
        srePath: action.payload.srePath || state.srePath
      });
    case actions.INIT_SRE_SUCCESS:
      return _extends({}, state, {
        loading: false,
        loadingPercent: 100,
        initialized: true
      });
    case actions.SRE_LOAD_STATUS:
      return _extends({}, state, { loadingPercent: action.payload });
    case actions.INIT_SRE_FAILURE:
      return _extends({}, state, { error: action.payload, loading: false });

    case actions.SET_VERSION:
      return _extends({}, state, {
        version: action.payload.version
      });

    /* istanbul ignore next */
    default:
      return state;
  }
};

var reducer = function reducer() {
  var _extends5;

  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : initial;
  var action = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

  switch (action && action.type) {
    // case actions.SRE_SHOW_BROWSER_MIC_BLOCK_MODAL:
    // case actions.SRE_BROWSER_MIC_BLOCK_MODAL_CHOICE:
    case actions.INIT_SRE:
    case actions.INIT_SRE_SUCCESS:
    case actions.SRE_LOAD_STATUS:
    case actions.INIT_SRE_FAILURE:
    case actions.SET_VERSION:
    case actions.SRE_SONIC_LOAD_COMPLETE:
    case actions.SRE_SPEECH_MODEL_LOAD_COMPLETE:
      return initSREReducer(state, action);

    case actions.SRE_OPEN_CALIBRATE_WIN:
    case actions.SRE_CLOSE_CALIBRATE_WIN:
    case actions.SRE_CALIBRATE_START:
    case actions.SRE_CALIBRATE_COMPLETE:
    case actions.SRE_CALIBRATE_CLOSE_HELP:
    case actions.SRE_CALIBRATE_HELP:
    case actions.SRE_CALIBRATE_ERROR:
      return calibrateReducer(state, action);

    case actions.SRE_LISTEN_FOR_PHRASES:
    case actions.SRE_LISTEN_FOR_PHRASE_RESULT:
    case actions.SRE_LISTEN_FOR_PHRASES_RESET_WORDS_RESULT_CACHE:
    case actions.SRE_RESET_RECENTLY_SAVED_SOUND_LOG_IDS_CACHE:
    case actions.SRE_ABORT_LISTEN:
      return listenReducer(state, action);

    case SreReadingTracker.READING_TRACKER_SESSION_START_V2:
    case SreReadingTracker.READING_TRACKER_SESSION_PROGRESS_V2:
    case SreReadingTracker.READING_TRACKER_SESSION_RESET:
    case SreReadingTracker.READING_TRACKER_SESSION_RESULT_V2:
    case SreReadingTracker.READING_TRACKER_PLAYBACK_INTERRUPT:
    case SreReadingTracker.READING_TRACKER_PLAYBACK_START_V2:
    case SreReadingTracker.READING_TRACKER_PLAYBACK_PROGRESS:
    case SreReadingTracker.READING_TRACKER_PLAYBACK_END:
    case SreReadingTracker.READING_TRACKER_SESSION_STORE_SOUND:
    case SreReadingTracker.READING_TRACKER_PLAYBACK_SEEK_TIME:
      return SreReadingTracker.reducer(state, action);

    case SrePlaySound.PLAY_SOUND_SESSION_STORE_SOUND:
    case SrePlaySound.PLAY_SOUND_SESSION_START:
    case SrePlaySound.PLAY_SOUND_SESSION_PROGRESS:
    case SrePlaySound.PLAY_SOUND_SESSION_END:
      return SrePlaySound.reducer(state, action);

    case SreAudioQualityModals.SRE_SHOW_SILENCE_MODAL:
    case SreAudioQualityModals.SRE_HIDE_SILENCE_MODAL:
    case SreAudioQualityModals.SRE_SHOW_TOO_LOUD_MODAL:
    case SreAudioQualityModals.SRE_HIDE_TOO_LOUD_MODAL:
    case SreAudioQualityModals.SRE_SHOW_TOO_SOFT_MODAL:
    case SreAudioQualityModals.SRE_HIDE_TOO_SOFT_MODAL:
    case SreAudioQualityModals.SRE_SHOW_TOO_NOISY_MODAL:
    case SreAudioQualityModals.SRE_HIDE_TOO_NOISY_MODAL:
    case SreAudioQualityModals.SRE_SHOW_DISABLE_SPEECH_FOR_NOW_MODAL:
    case SreAudioQualityModals.SRE_HIDE_DISABLE_SPEECH_FOR_NOW_MODAL:
    case SreAudioQualityModals.SRE_SHOW_SUBMIT_REPORT_MODAL:
    case SreAudioQualityModals.SRE_HIDE_SUBMIT_REPORT_MODAL:
    case SreAudioQualityModals.SRE_INCREMENT_NUMBER_REPORTS_SUBMITTED:
    case SreAudioQualityModals.SRE_RESET_NUMBER_REPORTS_SUBMITTED:
      return SreAudioQualityModals.reducer(state, action);

    case actions.SRE_BEGIN_LOADING:
      return _extends({}, state, {
        loadingPercent: 0,
        loading: true
      });

    case actions.SRE_MIC_ACCESS:
      return _extends({}, state, { micAccess: action.payload });

    case actions.SRE_AUDIO_LEVEL:
      return _extends({}, state, { audioLevel: action.payload });

    case actions.SRE_MIC_LIST:
      return _extends({}, state, { availableMicrophones: unique(action.payload.map(function (inputDevice) {
          return inputDevice.name;
        })) });

    case actions.SRE_MIC_CHOSEN:
      return _extends({}, state, { selectedMicrophone: action.payload });

    case actions.SRE_HYPOTHESIS:
      return _extends({}, state, { hypothesis: action.payload });

    case actions.SRE_LISTEN_FOR_PHRASES_UPDATE:
      // NOTE - there is additional info in the payload that comes in. However, we don't
      //        use it anywhere, so we're not storing it.
      return _extends({}, state, {
        hypothesis: action.payload.response,
        audioLevel: action.payload.energy || 0
      });

    case actions.SRE_SPEECH_DISABLE:
      return _extends({}, state, { speechEnabled: action.payload.enabled });

    case actions.SRE_RESET_PROMPT_RESULT:
      return _extends({}, state, {
        scores: _extends({}, state.scores, (_extends5 = {}, _extends5[action.payload.promptId] = null, _extends5))
      });

    case actions.SRE_ENABLE_FAKE:
      return _extends({}, state, { fakeSRE: true });

    case actions.SRE_SHOW_BROWSER_MIC_BLOCK_MODAL:
      return _extends({}, state, {
        isBrowserMicBlockModalVisible: extractPath(action, "payload.show")
      });

    case actions.SRE_USER_ACKNOWLEDGES_BROWSER_MIC_BLOCK_MODAL:
      return _extends({}, state, {
        browserMicBlockModalChoice: extractPath(action, "payload.choice"),
        isBrowserMicBlockModalVisible: false
      });

    default:
      return state;
  }
};

var _selectors = {
  srePath: function srePath(state) {
    return state.srePath;
  },
  isFakeSRE: function isFakeSRE(state) {
    return state.fakeSRE;
  },
  isSREInitialized: function isSREInitialized(state) {
    return state.initialized;
  },
  isSRELoading: function isSRELoading(state) {
    return state.loading;
  },
  isSREListening: function isSREListening(state) {
    return state.listening;
  },
  isSREListeningToPrompt: function isSREListeningToPrompt(state, promptId) {
    return state.currentPromptId === promptId && state.listening;
  },
  isSRECalibrated: function isSRECalibrated(state) {
    return state.calibrated;
  },
  isSpeechEnabled: function isSpeechEnabled(state) {
    return state.speechEnabled;
  },
  isSREHelpOpen: function isSREHelpOpen(state) {
    return state.calibrateHelp;
  },
  isSREExitOnCancel: function isSREExitOnCancel(state) {
    return state.exitOnCancelCalibrate;
  },
  isSREExitOnDisableSpeech: function isSREExitOnDisableSpeech(state) {
    return state.exitOnDisableSpeechCalibrate;
  },
  sreVersion: function sreVersion(state) {
    return state.version;
  },
  sreAudioLevel: function sreAudioLevel(state) {
    return state.audioLevel;
  },
  sreAudioQualityMetrics: function sreAudioQualityMetrics(state) {
    return state.audioQualityMetrics;
  },
  sreAudioLevelForPrompt: function sreAudioLevelForPrompt(state, promptId) {
    return state.currentPromptId === promptId && state.audioLevel;
  },
  sreScore: function sreScore(state, promptId) {
    return state.scores[promptId];
  },
  sreWordScores: function sreWordScores(state, promptId) {
    return state.wordScores[promptId];
  },
  sreCalibrateResult: function sreCalibrateResult(state) {
    return state.calibrateResult;
  },
  sreCalibrateError: function sreCalibrateError(state) {
    return state.calibrateError;
  },
  sreLoadingPercent: function sreLoadingPercent(state) {
    return state.loadingPercent;
  },
  sreMicAccess: function sreMicAccess(state) {
    return state.micAccess;
  },
  sreMicList: function sreMicList(state) {
    return state.availableMicrophones;
  },
  sreError: function sreError(state) {
    return state.error;
  },
  sreSaveSoundLogID: function sreSaveSoundLogID(state) {
    return state.saveSoundLogID;
  },
  recentlySavedSoundLogIDs: function recentlySavedSoundLogIDs(state) {
    return state.recentlySavedSoundLogIDs;
  },
  sreSelectedMicrophone: function sreSelectedMicrophone(state) {
    return state.selectedMicrophone;
  },
  sreHypothesis: function sreHypothesis(state) {
    return state.hypothesis;
  },
  sreCalibrating: function sreCalibrating(state) {
    return state.calibratingMicrophone;
  },
  sreCalibrateFailureCount: function sreCalibrateFailureCount(state) {
    return state.failureCount;
  },
  failedCalibration: function failedCalibration(state) {
    return ["ok", ""].indexOf(state.calibrateResult) === -1;
  },
  successfulCalibration: function successfulCalibration(state) {
    return state.calibrateResult === "ok";
  },
  sreLastModelLoaded: function sreLastModelLoaded(state) {
    return state.lastSpeechModelLoaded;
  },
  hasSpeechModelLoaded: function hasSpeechModelLoaded(state) {
    return state.speechModelLoaded;
  },
  hasSonicLoaded: function hasSonicLoaded(state) {
    return state.sonicLoaded;
  },
  isMicDeniedInBrowserByUser: function isMicDeniedInBrowserByUser(state) {
    var micList = _selectors.sreMicList(state);
    if (micList && micList.length > 0 && micList.includes("Browser Selected Microphone")) {
      return true;
    }
    return false;
  },
  isBrowserMicBlockModalVisible: function isBrowserMicBlockModalVisible(state) {
    return state.isBrowserMicBlockModalVisible;
  }
};

var wrapper = function wrapper(selector) {
  return function (state) {
    for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    return selector.apply(undefined, [state.sre].concat(args));
  };
};

export var selectors = _extends({}, wrapSelectors(wrapper, _selectors));

export var actionCreators = _Object$keys(actions).reduce(function (acc, curr) {
  var creator = actions[curr];
  if (typeof creator === "function") {
    acc[curr] = creator;
  }
  return acc;
}, {});

export default reducer;